/**
 * The account's keys, at app.easting.ai/keys.
 *
 * The plaintext is shown once — at signup, at the claim page, and here after
 * a mint or a rotate — and never again, so a customer who loses one needs a
 * rotate button rather than a support thread. The client routes existed
 * before this page did and nothing called them, which left a lost free-tier
 * key a dead end (`docs/plans/implemented/FREE_TIER.md`).
 *
 * A second key is never a second allowance. Quota pools at the account, so
 * minting buys separation — a key for the QGIS plugin and a key for a nightly
 * script — and nothing else. A free account holds exactly one; the server
 * explains that in its own words when asked for a second, and this page shows
 * those words rather than paraphrasing them.
 */

import { useCallback, useEffect, useState } from "preact/hooks";
import { ApiError, type Api } from "../api";
import type { KeyRow, MintResponse } from "../types";

export interface KeysProps {
  api: Api;
  onExit: () => void;
}

export function Keys({ api, onExit }: KeysProps) {
  const [keys, setKeys] = useState<KeyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The plaintext from the last mint or rotate, alive until dismissed. Kept
  // out of the table: rows render what the server lists, and the server
  // never lists a secret.
  const [minted, setMinted] = useState<MintResponse | null>(null);
  const [minting, setMinting] = useState(false);

  const reload = useCallback(
    () =>
      api
        .keys()
        .then(setKeys)
        .catch((failure) => setError(describe(failure))),
    [api],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  async function act(
    prompt: string,
    action: () => Promise<MintResponse | void>,
  ) {
    // The console asks before every mutation and so does this: revoking the
    // key a nightly script runs on is not something to do by mis-click.
    if (!window.confirm(prompt)) return;
    setError(null);
    try {
      const result = await action();
      if (result) setMinted(result);
      await reload();
    } catch (failure) {
      setError(describe(failure));
    }
  }

  async function mint(name: string) {
    setMinting(false);
    setError(null);
    try {
      setMinted(await api.mintKey(name));
      await reload();
    } catch (failure) {
      // On a free account this is the 409 that names the one-key rule and the
      // way out, which is a better sentence than any this file could write.
      setError(describe(failure));
    }
  }

  const rows = keys ?? [];
  const live = rows.filter((row) => !row.revoked_at);
  const revoked = rows.filter((row) => row.revoked_at);

  return (
    <div class="page">
      <h2>API keys</h2>
      <p class="muted">
        The credentials this account holds. A second key is never a second
        allowance — quota stays pooled at the account — it buys separation: a
        key for the QGIS plugin and a key for a nightly script, so revoking one
        leaves the other working.
      </p>

      {error && (
        <div class="error" role="alert">
          {error}
          <button type="button" class="ghost" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {minted && (
        <PlaintextOnce minted={minted} onDone={() => setMinted(null)} />
      )}

      {keys === null && !error && <p class="muted">Reading the keys…</p>}
      {keys !== null && !live.length && (
        <p class="muted">No live keys. Mint one below to use the API again.</p>
      )}

      {!!live.length && (
        <table class="calls">
          <thead>
            <tr>
              <th>Key</th>
              <th>Created</th>
              <th>Allowance</th>
              <th class="right">This month</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {live.map((row) => (
              <tr key={row.prefix}>
                <td>
                  <div class="mono">{row.prefix}</div>
                  <div class="meta muted">
                    {row.name}
                    {row.grant_kind === "free_signup" && " · free signup"}
                  </div>
                </td>
                <td>{when(row.created_at)}</td>
                <td>{allowance(row)}</td>
                <td class="right">{row.documents}</td>
                <td class="right">
                  <button
                    type="button"
                    class="link"
                    title="Same name, same allowance, new secret. This is the recovery path for a lost key."
                    onClick={() =>
                      act(
                        `Rotate ${row.prefix}? The old key stops working immediately, and the new one is shown once.`,
                        () => api.rotateKey(row.prefix),
                      )
                    }
                  >
                    Rotate
                  </button>{" "}
                  <button
                    type="button"
                    class="link"
                    onClick={() =>
                      act(
                        `Revoke ${row.prefix}? Anything using it starts failing at once.`,
                        () => api.revokeKey(row.prefix),
                      )
                    }
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {minting ? (
        <MintForm onSubmit={mint} onCancel={() => setMinting(false)} />
      ) : (
        keys !== null && (
          <p>
            <button
              type="button"
              class="secondary"
              onClick={() => setMinting(true)}
            >
              Mint a key…
            </button>
          </p>
        )
      )}

      {!!revoked.length && (
        <details>
          <summary class="muted">
            {revoked.length} retired key{revoked.length === 1 ? "" : "s"}
          </summary>
          {/* Kept listed on purpose, exactly as the server keeps the rows:
              the credential someone retired last week is the one they want
              to see when working out what is still live. */}
          <table class="calls">
            <thead>
              <tr>
                <th>Key</th>
                <th>Created</th>
                <th>Revoked</th>
              </tr>
            </thead>
            <tbody>
              {revoked.map((row) => (
                <tr key={row.prefix} class="muted">
                  <td>
                    <div class="mono">{row.prefix}</div>
                    <div class="meta">{row.name}</div>
                  </td>
                  <td>{when(row.created_at)}</td>
                  <td>{when(row.revoked_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <p>
        <button type="button" class="ghost" onClick={onExit}>
          Back
        </button>
      </p>
    </div>
  );
}

/** The plaintext exists here and nowhere else, the same promise the claim
 *  page and the signup card make. Dismissing it is final; the rotate button
 *  is the recovery path. */
function PlaintextOnce({
  minted,
  onDone,
}: {
  minted: MintResponse;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div class="banner">
      <strong>{minted.name}</strong> is ready. Copy the key now: this is the
      only time it is shown, and it cannot be recovered.
      <pre class="mono plaintext" data-testid="minted-key">
        {minted.api_key}
      </pre>
      <button
        type="button"
        class="ghost"
        onClick={() =>
          void navigator.clipboard
            ?.writeText(minted.api_key)
            .then(() => setCopied(true))
        }
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <button type="button" class="ghost" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

function MintForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  return (
    // The console's form styles, because it is the console's form with the
    // caps removed: a second CSS block saying the same thing would drift.
    <form
      class="card admin-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(name.trim());
      }}
    >
      <label>
        Name
        <input
          required
          value={name}
          placeholder="Nightly ETL"
          onInput={(event) => setName((event.target as HTMLInputElement).value)}
        />
      </label>
      <div class="admin-actions">
        <button type="submit" class="primary">
          Mint
        </button>
        <button type="button" class="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The console's words for the same rows, so the two surfaces agree. */
function allowance(row: KeyRow): string {
  if (row.pool_docs != null) return `${row.pool_docs} pooled, never expires`;
  if (row.included_docs != null)
    return `${row.included_docs} included, ${row.monthly_cap} ceiling`;
  return `${row.monthly_cap} a month`;
}

function when(iso?: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleDateString();
}

function describe(failure: unknown): string {
  if (failure instanceof ApiError) return failure.message;
  if (failure instanceof Error) return failure.message;
  return "Something went wrong.";
}
